import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Tags } from '@/features/tags/types/tags.types';

import { createSoftTag, validateCode } from './createSoftTag.thunk';
import type { CreateSoftTagResponse, ValidateCodeResponse } from '../utils/types';
import { mapSoftTagsResponseToState } from '../utils/helpers';

interface CreateSoftTagState {
  newSoftTags: Tags[];
  loading: boolean;
  error: string | null;
  validation: {
    loading: boolean;
    error: string | null;
    isValid: boolean | null;
  };
}
const initialState: CreateSoftTagState = {
  newSoftTags: [],
  loading: false,
  error: null,
  validation: {
    loading: false,
    error: null,
    isValid: null,
  },
};

const createSoftTagSlice = createSlice({
  name: 'createSoftTag',
  initialState,
  reducers: {
    resetValidation: state => {
      state.validation = {
        loading: false,
        error: null,
        isValid: null,
      };
    },
  },
  extraReducers: builder => {
    builder

      .addCase(createSoftTag.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createSoftTag.fulfilled,
        (state, action: PayloadAction<CreateSoftTagResponse>) => {
          state.loading = false;
          state.error = null;
          state.newSoftTags = action.payload
            ? [mapSoftTagsResponseToState(action.payload), ...state.newSoftTags]
            : state.newSoftTags;
        }
      )
      .addCase(createSoftTag.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to create soft tag';
      })

      .addCase(validateCode.pending, state => {
        state.validation.loading = true;
        state.validation.error = null;
        state.validation.isValid = null;
      })
      .addCase(
        validateCode.fulfilled,
        (state, action: PayloadAction<ValidateCodeResponse>) => {
          state.validation.loading = false;
          state.validation.error = action.payload.valid ? null : action.payload.error;
          state.validation.isValid = action.payload.valid;
        }
      )
      .addCase(validateCode.rejected, (state, action) => {
        state.validation.loading = false;
        state.validation.error = (action.payload as any)?.message || 'Failed to validate code';
        state.validation.isValid = false;
      });
  },
});

// Export actions
export const { resetValidation } = createSoftTagSlice.actions;

// Export reducer for store
export default createSoftTagSlice.reducer;
